import { useState } from 'react';
import { formatearImporteDesdeCents, t, type Idioma } from '@matias-asset-os/i18n';
import { Icono } from '../componentes/Iconos.js';
import { fechaLarga } from '../componentes.js';
import {
  estadoAccion,
  etapaActiva,
  frescuraDe,
  proximaAccion,
  type EstadoAccion,
  type Etapa,
  type Obra,
} from './modelo.js';
import { puntuar } from './puntuacion.js';

/**
 * Las piezas pequenas que salen en el mapa, en la lista y en la ficha.
 *
 * Viven juntas para que la misma cosa se vea igual en los tres sitios: si la
 * puntuacion se pinta de un color en la lista y de otro en la ficha, deja de
 * ser un dato y pasa a ser decoracion.
 */

// --- Puntuacion --------------------------------------------------------------

/** Clase de pastilla por prioridad. Los tres colores del sistema, sin inventar. */
const CLASE_PRIORIDAD: Record<'ALTA' | 'MEDIA' | 'BAJA', string> = {
  ALTA: 'ROJO',
  MEDIA: 'AMARILLO',
  BAJA: 'NEUTRA',
};

export function Puntuacion({
  obra,
  idioma,
  hoy,
  compacta = false,
}: {
  obra: Obra;
  idioma: Idioma;
  hoy: string;
  compacta?: boolean;
}) {
  const [abierta, setAbierta] = useState(false);
  const p = puntuar(obra, hoy);

  return (
    <span className="obra-puntuacion">
      <button
        type="button"
        className={`pastilla ${CLASE_PRIORIDAD[p.prioridad]} puntuacion-boton`}
        aria-expanded={abierta}
        title={t(idioma, 'obras.porQue')}
        onClick={(e) => {
          e.stopPropagation();
          setAbierta((v) => !v);
        }}
      >
        <b>{p.total}</b>
        {!compacta && <span>{t(idioma, `obras.prioridad.${p.prioridad}`)}</span>}
      </button>

      {abierta && (
        <div className="puntuacion-detalle" role="dialog" aria-label={t(idioma, 'obras.porQue')}>
          <div className="puntuacion-cabeza">
            <b>
              {p.total} · {t(idioma, `obras.prioridad.${p.prioridad}`)}
            </b>
            <button
              type="button"
              className="modal-cerrar"
              aria-label={t(idioma, 'tarea.cerrar')}
              onClick={(e) => {
                e.stopPropagation();
                setAbierta(false);
              }}
            >
              <Icono nombre="cerrar" />
            </button>
          </div>
          <ul className="puntuacion-motivos">
            {p.motivos.map((m, i) => (
              <li key={`${m.clave}-${i}`} className={m.puntos < 0 ? 'resta' : m.puntos > 0 ? 'suma' : 'neutro'}>
                <span className="signo" aria-hidden="true">
                  {m.puntos > 0 ? '+' : m.puntos < 0 ? '−' : '·'}
                </span>
                <span className="motivo">{t(idioma, m.clave, m.datos ?? {})}</span>
                <span className="puntos numerico">{m.puntos === 0 ? '' : Math.abs(m.puntos)}</span>
              </li>
            ))}
          </ul>
          <p className="suave pequeno">{t(idioma, 'obras.comoSeCalcula')}</p>
        </div>
      )}
    </span>
  );
}

// --- Etapa comercial ---------------------------------------------------------

const CLASE_ETAPA: Record<Etapa, string> = {
  DETECTADA: 'NEUTRA',
  INVESTIGANDO: 'NEUTRA',
  PENDIENTE_CONTACTAR: 'AMARILLO',
  CONTACTADA: 'AZUL',
  CONVERSACION: 'AZUL',
  REUNION: 'AZUL',
  PROPUESTA: 'AMARILLO',
  GANADA: 'VERDE',
  PERDIDA: 'ROJO',
  PAUSA: 'NEUTRA',
};

export function PastillaEtapa({ etapa, idioma }: { etapa: Etapa; idioma: Idioma }) {
  return (
    <span className={`pastilla ${CLASE_ETAPA[etapa]}`}>{t(idioma, `obras.etapa.${etapa}`)}</span>
  );
}

// --- Progreso ----------------------------------------------------------------

/**
 * Barra de progreso con el numero AL LADO, no dentro.
 *
 * Dentro de la barra el numero cambia de color de fondo segun cuanto haya
 * avanzado, y a la mitad queda ilegible sobre el borde del relleno.
 */
export function Progreso({ obra, idioma }: { obra: Obra; idioma: Idioma }) {
  return (
    <span className="obra-progreso" title={t(idioma, `obras.fase.${obra.fase}`)}>
      <span className="obra-progreso-pista" aria-hidden="true">
        <span className="obra-progreso-valor" style={{ width: `${obra.progreso}%` }} />
      </span>
      <span className="numerico">{obra.progreso} %</span>
    </span>
  );
}

// --- Frescura ----------------------------------------------------------------

const CLASE_FRESCURA = {
  RECIENTE: 'VERDE',
  VERIFICAR: 'AMARILLO',
  DESACTUALIZADA: 'ROJO',
  SIN_VERIFICAR: 'NEUTRA',
} as const;

export function Frescura({ obra, idioma, hoy }: { obra: Obra; idioma: Idioma; hoy: string }) {
  const f = frescuraDe(obra, hoy);
  return (
    <span className={`pastilla ${CLASE_FRESCURA[f]}`} title={fechaLarga(obra.ultimaComprobacion, idioma)}>
      {t(idioma, `obras.frescura.${f}`)}
    </span>
  );
}

// --- Proxima accion ----------------------------------------------------------

const CLASE_ACCION: Record<EstadoAccion, string> = {
  VENCIDA: 'ROJO',
  HOY: 'AMARILLO',
  PROXIMA: 'AZUL',
  SIN_ACCION: 'NEUTRA',
};

/**
 * Que hay que hacer ahora, quien y cuando. Las tres cosas o ninguna: media
 * respuesta —«llamar», sin decir quien ni cuando— no mueve nada.
 */
export function ProximaAccion({
  obra,
  idioma,
  hoy,
  detallada = false,
}: {
  obra: Obra;
  idioma: Idioma;
  hoy: string;
  detallada?: boolean;
}) {
  const accion = proximaAccion(obra);
  const estado = estadoAccion(obra, hoy);
  const viva = etapaActiva(obra.etapa);

  if (!accion) {
    return (
      <span className={`pastilla ${viva ? 'AMARILLO' : 'NEUTRA'}`}>
        {t(idioma, 'obras.sinProxima')}
      </span>
    );
  }

  return (
    <span className="obra-accion">
      <span className={`pastilla ${CLASE_ACCION[estado]}`}>
        {t(idioma, `obras.estadoAccion.${estado}`)}
      </span>
      <span className="obra-accion-texto">
        <b>{t(idioma, `obras.accionTipo.${accion.tipo}`)}</b>
        {detallada && <span> · {accion.descripcion}</span>}
        <small>
          {fechaLarga(accion.cuando, idioma)} · {accion.responsable}
        </small>
      </span>
    </span>
  );
}

// --- Dinero ------------------------------------------------------------------

export function Dinero({
  cents,
  idioma,
}: {
  cents: number | null;
  idioma: Idioma;
}) {
  if (cents === null) return <span className="suave">{t(idioma, 'obras.sinDato')}</span>;
  return <span className="numerico">{formatearImporteDesdeCents(cents, idioma)}</span>;
}

// --- Ternario ----------------------------------------------------------------

/**
 * Si, no, o todavia no se sabe.
 *
 * Tres botones y no una casilla, porque una casilla solo sabe decir si o no: lo
 * que todavia no se ha averiguado acabaria contado como «no», que es una
 * afirmacion que nadie ha hecho.
 */
export function Ternario({
  etiqueta,
  valor,
  idioma,
  onCambio,
}: {
  etiqueta: string;
  valor: boolean | null;
  idioma: Idioma;
  onCambio: (v: boolean | null) => void;
}) {
  const opciones: Array<{ v: boolean | null; texto: string }> = [
    { v: true, texto: t(idioma, 'obras.si') },
    { v: false, texto: t(idioma, 'obras.no') },
    { v: null, texto: t(idioma, 'obras.desconocido') },
  ];
  return (
    <div className="campo campo-ternario">
      <span className="etiqueta-campo">{etiqueta}</span>
      <div className="conmutador-ternario" role="group" aria-label={etiqueta}>
        {opciones.map((o) => (
          <button
            key={String(o.v)}
            type="button"
            aria-pressed={valor === o.v}
            onClick={() => onCambio(o.v)}
          >
            {o.texto}
          </button>
        ))}
      </div>
    </div>
  );
}
